import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, ChefHat, ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import PublicHeader from "@/components/PublicHeader";
import TagsMenuBar from "@/components/TagsMenuBar";
import FeaturedSidebar from "@/components/FeaturedSidebar";
import ReviewsSidebar from "@/components/ReviewsSidebar";
import BrandHero from "@/components/BrandHero";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Strip HTML to plain text for the card excerpt.
function htmlExcerpt(html, max = 180) {
  if (!html) return "";
  const stripped = html
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, " ")
    .trim();
  return stripped.length > max ? stripped.slice(0, max).trim() + "…" : stripped;
}

// Head tags for the open post (SEO / social sharing). Reverted when the post closes.
function setPostSeo(post) {
  const prev = document.title;
  const tags = [];
  const upsert = (sel, attrs) => {
    let el = document.head.querySelector(sel);
    if (!el) { el = document.createElement(attrs.rel ? "link" : attrs.type ? "script" : "meta"); document.head.appendChild(el); tags.push(el); }
    Object.entries(attrs).forEach(([k, v]) => { if (k === "text") el.textContent = v; else el.setAttribute(k, v); });
    el.setAttribute("data-post-seo", "1");
    return el;
  };
  const url = post.url || `https://orevital.com/post/${post.url_slug || post.slug}`;
  const desc = post.seo_description || htmlExcerpt(post.html, 160);
  const img = post.thumbnail_url || post.image_url || "";
  document.title = `${post.title || post.slug} · OreVital Blog`;
  upsert('meta[name="description"]', { name: "description", content: desc });
  upsert('link[rel="canonical"]', { rel: "canonical", href: url });
  upsert('meta[property="og:type"]', { property: "og:type", content: "article" });
  upsert('meta[property="og:title"]', { property: "og:title", content: post.title || post.slug });
  upsert('meta[property="og:description"]', { property: "og:description", content: desc });
  upsert('meta[property="og:url"]', { property: "og:url", content: url });
  if (img) upsert('meta[property="og:image"]', { property: "og:image", content: img });
  upsert('meta[name="twitter:card"]', { name: "twitter:card", content: img ? "summary_large_image" : "summary" });
  upsert('script[type="application/ld+json"][data-post-seo]', { type: "application/ld+json", text: JSON.stringify({
    "@context": "https://schema.org", "@type": "BlogPosting", headline: post.title, description: desc, image: img || undefined,
    url, mainEntityOfPage: url, datePublished: post.created_at || undefined, dateModified: post.updated_at || undefined,
    publisher: { "@type": "Organization", name: "OreVital", url: "https://orevital.com" },
  }) });
  return () => { document.title = prev; document.head.querySelectorAll("[data-post-seo]").forEach((el) => el.remove()); };
}

export default function Blog() {
  const dealer = new URLSearchParams(useLocation().search).get("d") || "orevital";
  const navigate = useNavigate();
  const { slug: routeSlug } = useParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [single, setSingle] = useState(null); // post loaded directly from /post/<slug> (may not be on the blog list)
  const urlSlug = (v) => (v || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || (v || "").toLowerCase();
  const active = routeSlug ? urlSlug(decodeURIComponent(routeSlug)) : null;
  const setActive = (slug) => navigate(slug ? `/post/${urlSlug(slug)}` : "/blog");

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/postcue/posts?share_only=true`);
        if (!cancel) setPosts(data.items || []);
      } catch {
        if (!cancel) setPosts([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const activePost = posts.find((p) => urlSlug(p.slug) === active) || (single && urlSlug(single.slug) === active ? single : null);

  // Direct link to a post that isn't in the blog feed (e.g. shared dealer post) → fetch it on its own.
  useEffect(() => {
    if (!active || loading || posts.some((p) => urlSlug(p.slug) === active)) return;
    let cancel = false;
    axios.get(`${API}/postcue/post/${encodeURIComponent(active)}`).then(({ data }) => { if (!cancel) setSingle(data.post); }).catch(() => { if (!cancel) setSingle(null); });
    return () => { cancel = true; };
  }, [active, loading, posts]);

  useEffect(() => {
    if (!activePost) return undefined;
    window.scrollTo({ top: 0 });
    return setPostSeo(activePost);
  }, [activePost]);

  return (
    <>
      <PublicHeader />
      <TagsMenuBar />
      <div className="max-w-6xl mx-auto px-4 py-6" data-testid="blog-page">
      <main className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 items-start">
        <div className="space-y-5">
      <BrandHero />

      <div className="flex items-center gap-2 mb-2">
        <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
          <ChefHat size={18} />
        </div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-zinc-900">
            Blog & Resources
          </h1>
          <p className="text-xs text-zinc-500">
            Skincare tips, wellness guides & curated picks.
          </p>
        </div>
      </div>

      {loading && (
        <div className="py-12 flex justify-center text-zinc-500">
          <Loader2 className="animate-spin" />
        </div>
      )}

      {!loading && posts.length === 0 && (
        <div className="rounded-xl bg-zinc-50 border border-zinc-200 p-6 text-center text-sm text-zinc-500">
          No posts available yet.
        </div>
      )}

      {!loading && !activePost && (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="blog-list">
          {posts.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => setActive(p.slug)}
                className="group w-full text-left rounded-2xl overflow-hidden bg-white border border-zinc-200 hover:border-emerald-400 hover:shadow-md transition-all"
                data-testid={`blog-card-${p.slug}`}
              >
                {(p.thumbnail_url || p.image_url) ? (
                  <div className="aspect-[16/10] bg-zinc-100 overflow-hidden">
                    <img
                      src={p.thumbnail_url || p.image_url}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="aspect-[16/10] bg-gradient-to-br from-emerald-50 to-violet-50 flex items-center justify-center">
                    <ChefHat size={36} className="text-emerald-300" />
                  </div>
                )}
                <div className="p-4">
                  <h3 className="font-semibold text-zinc-900 leading-snug mb-1.5 line-clamp-2">
                    {p.title || p.slug}
                  </h3>
                  <p className="text-xs text-zinc-500 line-clamp-3 leading-relaxed">
                    {htmlExcerpt(p.html)}
                  </p>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 group-hover:underline">
                    Read more <ChevronRight size={12} />
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {activePost && (
        <div data-testid="blog-detail">
          <button
            type="button"
            onClick={() => setActive(null)}
            className="text-xs font-semibold text-emerald-700 hover:underline mb-3"
            data-testid="blog-back-btn"
          >
            ← Back to all posts
          </button>
          <Card className="rounded-2xl overflow-hidden border-zinc-200">
            {activePost.image_url && !(activePost.html || "").includes(activePost.image_url) && (
              <img
                src={activePost.image_url}
                alt={activePost.title}
                className="w-full max-h-64 object-cover"
              />
            )}
            <div className="p-5">
              <h2 className="text-2xl font-semibold text-zinc-900 mb-3 tracking-tight">
                {activePost.title}
              </h2>
              {activePost.video_url && (
                <div className="aspect-video mb-4 rounded-xl overflow-hidden bg-black">
                  <iframe
                    src={activePost.video_url}
                    title={activePost.title}
                    className="w-full h-full"
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}
              <iframe
                title={`blog-${activePost.slug}`}
                srcDoc={activePost.html}
                sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms"
                referrerPolicy="no-referrer"
                scrolling="no"
                className="w-full block border-0"
                style={{ height: "60vh", overflow: "hidden" }}
                onLoad={(e) => {
                  const f = e.currentTarget;
                  const fit = () => {
                    try {
                      const doc = f.contentDocument;
                      if (!doc) return;
                      doc.documentElement.style.overflow = "hidden";
                      if (doc.body) doc.body.style.overflow = "hidden";
                      const h = Math.max(doc.documentElement.scrollHeight, doc.body?.scrollHeight || 0);
                      if (h > 0) f.style.height = `${h + 8}px`;
                    } catch { /* ignore */ }
                  };
                  fit();
                  setTimeout(fit, 600);
                  setTimeout(fit, 2000);
                  // In-page anchors (A–Z jump bar etc.): the frame is sized to its content, so scroll the parent.
                  try {
                    const doc = f.contentDocument;
                    doc?.addEventListener("click", (ev) => {
                      const a = ev.target.closest?.("a[href^='#']");
                      if (!a) return;
                      const el = doc.getElementById(decodeURIComponent(a.getAttribute("href").slice(1)));
                      if (!el) return;
                      ev.preventDefault();
                      const top = el.getBoundingClientRect().top + f.getBoundingClientRect().top + window.scrollY - 96;
                      window.scrollTo({ top, behavior: "smooth" });
                    }, true);
                  } catch { /* ignore */ }
                }}
                data-testid="blog-html-frame"
              />
            </div>
          </Card>
        </div>
      )}
        </div>

        {/* Right sidebar — same template as the Dealer page: Featured
            products carousel + Customer Reviews carousel. */}
        <aside className="hidden lg:block" data-testid="blog-sidebar">
          <div className="space-y-4">
            <FeaturedSidebar dealerSlug={dealer} />
            <ReviewsSidebar dealerSlug={dealer} />
          </div>
        </aside>
      </main>
      </div>
    </>
  );
}
